import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  MdTitle,
  MdDescription,
  MdFlag,
  MdCalendarToday,
  MdCheckCircle,
  MdErrorOutline,
  MdSend,
  MdPersonSearch,
  MdPeople,
  MdPlaylistAddCheck,
  MdAssignmentInd,
  MdKeyboardArrowDown,
  MdCheck,
  MdClose,
} from "react-icons/md";
import api from "../services/api";
import logo from "../assets/logo.png";
import "./PublicTaskSubmit.css";

const PRIORITIES = [
  { value: "low",      label: "Low",      color: "#10b981" },
  { value: "medium",   label: "Medium",   color: "#f59e0b" },
  { value: "high",     label: "High",     color: "#ef4444" },
  { value: "critical", label: "Critical", color: "#7c3aed" },
];

const STATUSES = [
  { value: "todo",        label: "To Do",       dot: "#94a3b8" },
  { value: "in-progress", label: "In Progress", dot: "#3b82f6" },
  { value: "review",      label: "Review",      dot: "#f59e0b" },
  { value: "done",        label: "Done",        dot: "#10b981" },
];

const EMPTY_FORM = {
  assignedBy:   "",
  assignedTo:   "",
  coAssignedTo: "",
  title:        "",
  description:  "",
  status:       "todo",
  priority:     "medium",
  deadline:     "",
};

// ── Reusable custom dropdown ────────────────────────────────────────────────
const CustomSelect = ({ value, onChange, options, placeholder, searchable = false, clearable = false }) => {
  const [open, setOpen]         = useState(false);
  const [query, setQuery]       = useState("");
  const ref                     = useRef(null);

  const selected = options.find((o) => o.value === value);

  const filtered = searchable && query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const close = useCallback(() => { setOpen(false); setQuery(""); }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [close]);

  const pick = (val) => { onChange(val); close(); };

  return (
    <div className={`pdd-wrap ${open ? "pdd-wrap--open" : ""}`} ref={ref}>
      <button
        type="button"
        className="pdd-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`pdd-trigger-text ${!selected ? "pdd-placeholder" : ""}`}>
          {selected ? (
            <span className="pdd-selected-row">
              {selected.dot && <span className="pdd-dot" style={{ background: selected.dot }} />}
              {selected.color && <span className="pdd-dot" style={{ background: selected.color }} />}
              {selected.label}
            </span>
          ) : placeholder}
        </span>
        {clearable && selected && (
          <span
            className="pdd-clear"
            role="button"
            aria-label="Clear"
            onClick={(e) => { e.stopPropagation(); onChange(""); }}
          >
            <MdClose size={14} />
          </span>
        )}
        <MdKeyboardArrowDown size={16} className="pdd-arrow" />
      </button>

      {open && (
        <div className="pdd-menu" role="listbox">
          {searchable && (
            <div className="pdd-search-wrap">
              <input
                className="pdd-search"
                autoFocus
                placeholder="Search…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          <div className="pdd-list">
            {filtered.length === 0 ? (
              <div className="pdd-empty">No results</div>
            ) : filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={o.value === value}
                className={`pdd-option ${o.value === value ? "pdd-option--active" : ""}`}
                onClick={() => pick(o.value)}
              >
                <span className="pdd-option-inner">
                  {o.dot   && <span className="pdd-dot" style={{ background: o.dot }} />}
                  {o.color && <span className="pdd-dot" style={{ background: o.color }} />}
                  <span className="pdd-option-text">
                    {o.label}
                    {o.sub && <span className="pdd-sub">{o.sub}</span>}
                  </span>
                </span>
                {o.value === value && <MdCheck size={14} className="pdd-check" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main component ──────────────────────────────────────────────────────────
const PublicTaskSubmit = () => {
  const [users, setUsers]               = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError]     = useState(false);

  const [form, setForm]               = useState(EMPTY_FORM);
  const [submitting, setSubmitting]   = useState(false);
  const [submitted, setSubmitted]     = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    api.get("/public/users")
      .then(({ data }) => setUsers(data.data || []))
      .catch(() => setUsersError(true))
      .finally(() => setUsersLoading(false));
  }, []);

  const setField = (name, val) => {
    setForm((f) => ({ ...f, [name]: val }));
    if (submitError) setSubmitError("");
  };

  const handleChange = (e) => setField(e.target.name, e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.assignedBy)   { setSubmitError("Please select who is assigning this task."); return; }
    if (!form.assignedTo)   { setSubmitError("Please select who this task is assigned to."); return; }
    if (!form.title.trim()) { setSubmitError("Task title is required."); return; }
    if (!form.deadline)     { setSubmitError("Deadline is required."); return; }

    setSubmitting(true);
    setSubmitError("");
    try {
      await api.post("/public/submit", form);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success screen ──
  if (submitted) {
    const assigneeName = users.find((u) => u._id === form.assignedTo)?.name || "the assignee";
    return (
      <div className="pts-page">
        <div className="pts-card pts-card--center">
          <div className="pts-icon-wrap pts-icon-wrap--success">
            <MdCheckCircle size={44} />
          </div>
          <h2 className="pts-success-title">Task Submitted!</h2>
          <p className="pts-success-sub">
            Your task has been assigned to <strong>{assigneeName}</strong>.<br />
            They will be notified shortly.
          </p>
          <button className="pts-btn pts-btn--primary pts-btn--wide"
            onClick={() => { setSubmitted(false); setForm(EMPTY_FORM); }}>
            Submit Another Task
          </button>
        </div>
      </div>
    );
  }

  // Build options for user dropdowns
  const toUserOption = (u) => ({ value: u._id, label: u.name, sub: u.designation || "" });

  const assignedByOpts  = users.map(toUserOption);
  const assignedToOpts  = users.filter((u) => u._id !== form.coAssignedTo).map(toUserOption);
  const coAssigneeOpts  = users.filter((u) => u._id !== form.assignedTo).map(toUserOption);

  const UserDropdown = ({ name, value, options, placeholder }) => {
    if (usersLoading) return <div className="pts-select-placeholder">Loading users…</div>;
    if (usersError)   return (
      <div className="pts-select-placeholder pts-select-placeholder--err">
        <MdErrorOutline size={14} /> Could not load users. Please refresh.
      </div>
    );
    return (
      <CustomSelect
        value={value}
        onChange={(val) => setField(name, val)}
        options={options}
        placeholder={placeholder}
        searchable
        clearable
      />
    );
  };

  return (
    <div className="pts-page">
      <div className="pts-card">

        {/* Header */}
        <div className="pts-header">
          <div className="pts-logo-wrap">
            <img src={logo} alt="Logo" className="pts-logo" />
          </div>
          <h1 className="pts-title">Create a Task</h1>
          <p className="pts-subtitle">Complete the form below and we'll make sure it reaches the right person.</p>
        </div>

        <form onSubmit={handleSubmit} className="pts-form">

          {/* ── Assignment ── */}
          <div className="pts-section-label">Assignment</div>

          <div className="pts-field-grid">
            <div className="pts-field">
              <label className="pts-label">
                <MdAssignmentInd size={15} color="#6366f1" /> Assigned By <span className="pts-req">*</span>
              </label>
              <UserDropdown name="assignedBy" value={form.assignedBy} options={assignedByOpts} placeholder="Who is assigning?" />
            </div>
            <div className="pts-field">
              <label className="pts-label">
                <MdPersonSearch size={15} color="#3b82f6" /> Assigned To <span className="pts-req">*</span>
              </label>
              <UserDropdown name="assignedTo" value={form.assignedTo} options={assignedToOpts} placeholder="Who should do it?" />
            </div>
          </div>

          <div className="pts-field">
            <label className="pts-label">
              <MdPeople size={15} color="#06b6d4" /> Co-Assign To <span className="pts-optional">(optional)</span>
            </label>
            <UserDropdown name="coAssignedTo" value={form.coAssignedTo} options={coAssigneeOpts} placeholder="None" />
          </div>

          {/* ── Task details ── */}
          <div className="pts-section-label" style={{ marginTop: 8 }}>Task Details</div>

          <div className="pts-field">
            <label className="pts-label">
              <MdTitle size={15} color="#8b5cf6" /> Task Title <span className="pts-req">*</span>
            </label>
            <input className="pts-input" name="title" placeholder="What needs to be done?"
              value={form.title} onChange={handleChange} />
          </div>

          <div className="pts-field">
            <label className="pts-label">
              <MdDescription size={15} color="#10b981" /> Description <span className="pts-optional">(optional)</span>
            </label>
            <textarea className="pts-textarea" name="description" rows={4}
              placeholder="Add more context, links, or requirements…"
              value={form.description} onChange={handleChange} />
          </div>

          <div className="pts-field-grid">
            <div className="pts-field">
              <label className="pts-label">
                <MdPlaylistAddCheck size={15} color="#14b8a6" /> Status
              </label>
              <CustomSelect
                value={form.status}
                onChange={(val) => setField("status", val)}
                options={STATUSES}
                placeholder="Select status"
              />
            </div>
            <div className="pts-field">
              <label className="pts-label">
                <MdFlag size={15} color="#f59e0b" /> Priority
              </label>
              <CustomSelect
                value={form.priority}
                onChange={(val) => setField("priority", val)}
                options={PRIORITIES}
                placeholder="Select priority"
              />
            </div>
          </div>

          <div className="pts-field">
            <label className="pts-label">
              <MdCalendarToday size={15} color="#ef4444" /> Deadline <span className="pts-req">*</span>
            </label>
            <input className="pts-input" type="date" name="deadline" value={form.deadline}
              onChange={handleChange} min={new Date().toISOString().split("T")[0]} />
          </div>

          {/* Priority chips */}
          <div className="pts-priority-bar">
            {PRIORITIES.map((p) => (
              <button key={p.value} type="button"
                className={`pts-prio-chip ${form.priority === p.value ? "active" : ""}`}
                style={form.priority === p.value ? { background: p.color, borderColor: p.color, color: "#fff" } : {}}
                onClick={() => setField("priority", p.value)}>
                {p.label}
              </button>
            ))}
          </div>

          {submitError && (
            <div className="pts-error-msg"><MdErrorOutline size={15} /> {submitError}</div>
          )}

          <button type="submit" className="pts-btn pts-btn--primary pts-btn--submit" disabled={submitting}>
            {submitting ? (
              <><div className="spinner" style={{ width: 16, height: 16, borderWidth: 2, borderColor: "rgba(255,255,255,0.3)", borderTopColor: "#fff" }} /> Submitting…</>
            ) : (
              <><MdSend size={17} /> Submit Task</>
            )}
          </button>
        </form>

        <p className="pts-footer-note">All submissions are forwarded to the assigned person.</p>
      </div>
    </div>
  );
};

export default PublicTaskSubmit;
